function safeText(value) {
   return String(value ?? "").trim();
}

function isValidHttpUrl(value) {
   const text = safeText(value);
   if (!text) return false;
   try {
      const url = new URL(text);
      return url.protocol === "http:" || url.protocol === "https:";
   } catch {
      return false;
   }
}

function isLikelyProfilePhotoUrl(value) {
   const text = safeText(value);
   if (!isValidHttpUrl(text)) return false;
   return /googleusercontent\.com|ggpht\.com|gravatar\.com|cloudinary\.com/i.test(
      text,
   );
}

const PHOTO_KEYS = [
   "picture",
   "photoURL",
   "photoUrl",
   "photo",
   "avatar",
   "avatarUrl",
   "avatarURL",
   "profilePicture",
   "profilePhoto",
   "profilePhotoUrl",
   "profileImage",
   "profileImageUrl",
   "googlePicture",
   "googleAvatar",
   "googleAvatarUrl",
   "oauthPicture",
   "image",
   "imageUrl",
   "pictureUrl",
];

const NESTED_PROFILE_KEYS = [
   "google",
   "oauth",
   "authProvider",
   "provider",
   "profile",
   "userProfile",
   "identity",
   "extras",
   "metadata",
   "meta",
];

const NESTED_PATHS = [
   ["profile", "picture"],
   ["profile", "photoURL"],
   ["profile", "photoUrl"],
   ["profile", "avatar"],
   ["userProfile", "picture"],
   ["userProfile", "photoURL"],
   ["extras", "picture"],
   ["extras", "photoURL"],
   ["extras", "avatar"],
   ["oauth", "picture"],
   ["google", "picture"],
   ["identity", "picture"],
];

function readPhotoFromObject(source) {
   if (!source || typeof source !== "object") return null;

   for (const key of PHOTO_KEYS) {
      const value = safeText(source[key]);
      if (isValidHttpUrl(value)) return value;
   }

   return null;
}

function readPhotoFromPaths(person) {
   for (const path of NESTED_PATHS) {
      let current = person;
      for (const key of path) {
         current = current?.[key];
      }
      const value = safeText(current);
      if (isValidHttpUrl(value)) return value;
   }
   return null;
}

function readPhotoFromIdentities(person) {
   const identities = person?.identities;
   if (!Array.isArray(identities)) return null;

   for (const identity of identities) {
      const direct = readPhotoFromObject(identity);
      if (direct) return direct;
   }

   return null;
}

function findPhotoUrlDeep(source, depth = 0, seen = new Set()) {
   if (!source || depth > 4) return null;
   if (typeof source === "string") {
      return isLikelyProfilePhotoUrl(source) ? safeText(source) : null;
   }
   if (typeof source !== "object") return null;
   if (seen.has(source)) return null;
   seen.add(source);

   for (const value of Object.values(source)) {
      if (typeof value === "string" && isLikelyProfilePhotoUrl(value)) {
         return safeText(value);
      }
      if (value && typeof value === "object") {
         const nested = findPhotoUrlDeep(value, depth + 1, seen);
         if (nested) return nested;
      }
   }

   return null;
}

/** Extrage URL-ul fotografiei Google/OAuth din obiectul utilizatorului. */
export function resolveUserPhotoUrl(person) {
   if (!person || typeof person !== "object") return null;

   const direct = readPhotoFromObject(person);
   if (direct) return direct;

   const fromPaths = readPhotoFromPaths(person);
   if (fromPaths) return fromPaths;

   for (const key of NESTED_PROFILE_KEYS) {
      const nested = person?.[key];
      const nestedPhoto = readPhotoFromObject(nested);
      if (nestedPhoto) return nestedPhoto;
   }

   const fromIdentities = readPhotoFromIdentities(person);
   if (fromIdentities) return fromIdentities;

   return findPhotoUrlDeep(person);
}
